import { Button, DetailsDrawer, NothingToShow, Pagination } from 'shared-ui';

import { FailedOrderPayload } from 'api/dod/dod.types';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import {
  ButtonsWrapper,
  DetailName,
  DetailText,
  DetailTextBold,
  DetailsItemWrapper,
} from '../components/detailsItem/DetailsItem.styles';
import { PanelSkeleton } from '../components/panelSkeleton/PanelSkeleton';
import { StateChip } from '../components/stateChip/StateChip';
import { TimeSeriesPanel } from '../components/timeSeriesPanel/TimeSeriesPanel';
import { dodReaderLabel, dodReasonLabel, formatDateTime, formatDurationSeconds } from '../overviewTab/dodFormat';
import { dodCommandLabel } from '../components/dodCommands';

import * as Styled from './QueuesTab.styles';
import { QueueDrawerProps } from './QueueDrawer.types';

const DETAIL_PAGE_SIZE = 5;

const fullId = (id: string | null | undefined): string => id || '—';

/**
 * Details panel for one TKD queue, published to the page shell through `setDetails`.
 *
 * Opened by the URL (`?queue=<rcpId>:<nlk>`), not by component state, so a shared link lands on
 * the same queue. The body is the app's details trio; the waiting orders are listed as rows of it
 * (the panel is 400 px wide, the page table does not fit there) and the actions come two per row,
 * like every other details panel.
 */
export const QueueDrawer = ({
  selected,
  detail,
  isLoading,
  page,
  onPageChange,
  depth,
  isDepthUnavailable,
  canModify,
  pendingAction,
  onAction,
  onShowInHistory,
  onClose,
}: QueueDrawerProps): JSX.Element | null => {
  const { formatMessage } = useLocale();

  if (!selected) {
    return null;
  }

  const unknownLabel = formatMessage({ id: AppMessages['dod.overview.unknown'] });
  const state = detail?.state;
  const orders = detail?.orders;
  const isPaused = state?.status === 'paused';
  const header = dodReaderLabel(detail?.tkdNazwa, selected.nlk);
  const lastError = dodReasonLabel(state?.lastError, formatMessage);
  const notYet = formatMessage({ id: AppMessages['dod.queues.drawer.notDrainedYet'] });
  const tkdStatusId = detail?.tkdStatus ? `dod.tkdStatus.${detail.tkdStatus}` : undefined;
  const tkdStatusLabel =
    tkdStatusId && tkdStatusId in AppMessages
      ? formatMessage({ id: AppMessages[tkdStatusId as keyof typeof AppMessages] })
      : detail?.tkdStatus;
  /**
   * Said only for the two machine reasons, where it is verified: the drain sweep re-checks both
   * preconditions (RCP connected, reader OK) and re-arms the queue (drain-tkd-queue.processor
   * sweepTkd). Other pause reasons are not promised anything here.
   */
  const autoResumeId =
    isPaused && state?.lastError === 'tkd-not-ok'
      ? 'dod.queues.drawer.autoResume.tkdNotOk'
      : isPaused && state?.lastError === 'rcp-not-connected'
      ? 'dod.queues.drawer.autoResume.rcpNotConnected'
      : undefined;

  const describeOrder = (order: FailedOrderPayload): string => {
    if (order.retryCount === 0 && !order.lastAttemptAt && !order.lastError) {
      return [
        `${formatMessage({ id: AppMessages['dod.queues.drawer.kad'] })} ${fullId(order.kadId)}`,
        formatMessage({ id: AppMessages['dod.queues.drawer.notAttempted'] }),
      ].join(' · ');
    }

    const lastAttempt = order.lastAttemptAt
      ? formatMessage(
          { id: AppMessages['dod.queues.relativeAgo'] },
          { time: formatDurationSeconds(Math.round((Date.now() - order.lastAttemptAt) / 1000), unknownLabel) },
        )
      : '—';
    const reason = dodReasonLabel(order.lastError, formatMessage);

    return [
      `${formatMessage({ id: AppMessages['dod.queues.drawer.kad'] })} ${fullId(order.kadId)}`,
      `${formatMessage({ id: AppMessages['dod.queues.drawer.attempts'] })} ${order.retryCount}/${order.maxRetries}`,
      `${formatMessage({ id: AppMessages['dod.queues.drawer.lastAttempt'] })} ${lastAttempt}`,
      ...(reason ? [reason] : []),
    ].join(' · ');
  };

  return (
    <DetailsDrawer isOpen header={header}>
      {isLoading && !detail ? (
        <PanelSkeleton height={220} />
      ) : (
        <>
          <DetailsItemWrapper>
            <DetailName>{formatMessage({ id: AppMessages['dod.queues.column.rcp'] })}</DetailName>
            <DetailText>
              {detail?.rcpNazwa ?? selected.rcpId} · NLK {selected.nlk}
            </DetailText>
          </DetailsItemWrapper>

          <DetailsItemWrapper>
            <DetailName>{formatMessage({ id: AppMessages['dod.queues.drawer.state'] })}</DetailName>
            {state ? <StateChip state={state.status} /> : <DetailText>{unknownLabel}</DetailText>}
          </DetailsItemWrapper>

          {autoResumeId && (
            <DetailsItemWrapper>
              <DetailName />
              <DetailText>{formatMessage({ id: AppMessages[autoResumeId] })}</DetailText>
            </DetailsItemWrapper>
          )}

          {tkdStatusLabel && (
            <DetailsItemWrapper>
              <DetailName>{formatMessage({ id: AppMessages['dod.queues.drawer.readerStatus'] })}</DetailName>
              <DetailText>{tkdStatusLabel}</DetailText>
            </DetailsItemWrapper>
          )}

          <DetailsItemWrapper>
            <DetailName>{formatMessage({ id: AppMessages['dod.queues.drawer.drainPod'] })}</DetailName>
            {/* Written when a pod starts a drain; a queue that never drained has no pod to name. */}
            <DetailText>{state?.drainPodId ?? '—'}</DetailText>
          </DetailsItemWrapper>

          <DetailsItemWrapper>
            <DetailName>{formatMessage({ id: AppMessages['dod.queues.drawer.lastDrain'] })}</DetailName>
            <DetailText>{formatDateTime(state?.lastDrainAt) ?? notYet}</DetailText>
          </DetailsItemWrapper>

          <DetailsItemWrapper>
            <DetailName>{formatMessage({ id: AppMessages['dod.queues.drawer.retryCount'] })}</DetailName>
            {/* The queue's own counter, the same number the table row shows - not the page's max. */}
            <DetailText>{state?.retryCount ?? unknownLabel}</DetailText>
          </DetailsItemWrapper>

          <DetailsItemWrapper>
            <DetailName>{formatMessage({ id: AppMessages['dod.queues.column.lastError'] })}</DetailName>
            <DetailText>{lastError ?? '—'}</DetailText>
          </DetailsItemWrapper>

          <ButtonsWrapper>
            <Button
              size="small"
              variant="contained"
              disabled={!canModify || pendingAction !== null}
              onClick={() => onAction('drain', selected)}
            >
              {formatMessage({ id: AppMessages['dod.queues.action.drain'] })}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!canModify || pendingAction !== null}
              onClick={() => onAction(isPaused ? 'resume' : 'pause', selected)}
            >
              {formatMessage({
                id: AppMessages[isPaused ? 'dod.queues.action.resume' : 'dod.queues.action.pause'],
              })}
            </Button>
          </ButtonsWrapper>

          <ButtonsWrapper>
            <Button size="small" onClick={() => onShowInHistory(selected)}>
              {formatMessage({ id: AppMessages['dod.queues.drawer.showInHistory'] })}
            </Button>
            <Button size="small" onClick={onClose}>
              {formatMessage({ id: AppMessages['dod.queues.drawer.close'] })}
            </Button>
          </ButtonsWrapper>

          <Styled.DrawerSection>
            <DetailTextBold>
              {formatMessage({ id: AppMessages['dod.queues.drawer.orders'] }, { count: orders?.total ?? 0 })}
            </DetailTextBold>

            {orders?.items.length ? (
              // Stacked, not the 150px label grid: a command name does not fit a 150px column.
              orders.items.map((order, index) => (
                <Styled.OrderItem key={`${order.numerRozkazu}-${order.kadId ?? ''}-${index}`}>
                  <DetailName>{dodCommandLabel(String(order.numerRozkazu).padStart(2, '0'))}</DetailName>
                  <DetailText>{describeOrder(order)}</DetailText>
                </Styled.OrderItem>
              ))
            ) : (
              <DetailText>{formatMessage({ id: AppMessages['dod.queues.drawer.orders.empty'] })}</DetailText>
            )}

            {(orders?.total ?? 0) > (orders?.pageSize ?? DETAIL_PAGE_SIZE) && (
              <Pagination
                page={page - 1}
                count={orders?.total ?? 0}
                rowsPerPage={orders?.pageSize ?? DETAIL_PAGE_SIZE}
                // The panel is 400 px wide; a page-size selector there would only take room.
                rowsPerPageOptions={[orders?.pageSize ?? DETAIL_PAGE_SIZE]}
                setPage={(nextPage) => onPageChange(nextPage + 1)}
                setRowsPerPage={() => undefined}
              />
            )}
          </Styled.DrawerSection>

          <Styled.DrawerSection>
            {isDepthUnavailable ? (
              <>
                <DetailTextBold>{formatMessage({ id: AppMessages['dod.queues.drawer.depthChart'] })}</DetailTextBold>
                <NothingToShow text={formatMessage({ id: AppMessages['dod.overview.unavailable'] })} />
              </>
            ) : (
              <TimeSeriesPanel
                title={formatMessage({ id: AppMessages['dod.queues.drawer.depthChart'] })}
                series={[{ key: 'depth', labels: {}, points: depth }]}
                seriesLabels={{ depth: formatMessage({ id: AppMessages['dod.queues.drawer.depthSeries'] }) }}
                area
                height={160}
                tickNumber={3}
              />
            )}
          </Styled.DrawerSection>
        </>
      )}
    </DetailsDrawer>
  );
};
